// Engine context: replaces the reference tool's DOM and globals. `v` are the control values, `ui`/`options` collect
// what the HTML writes into the page (visibility, warnings, select options), the flags are the HTML globals.

import type { Translation } from "../../../hooks/useTranslations";
import type { EngineCatalog } from "./catalog";
import type { ControlValues } from "./controls";
import type { CltLato, ConnectionType } from "./types";
import { createUiState, type UiState } from "./ui";

export type DiameterOption = { value: number; label: string };
export type LengthOption = { value: number; label: string; screwId: string | null };

// Option lists of the selects (in display order); the selected value is the matching key of EngineOutput.values.
export type EngineOptions = {
  matTipo: { 1: string[]; 2: string[] };
  matSpecie: { 1: string[]; 2: string[] };
  matClasse: { 1: string[]; 2: string[] };
  families: string[]; // #etaFamiglia
  diameters: DiameterOption[]; // #etaDiametro
  lengths: LengthOption[]; // #etaLunghezza
  washers: string[]; // #rondellaCodice (empty = no compatible washer, #rondellaField hidden)
  cltDisposizione: { 1: string[]; 2: string[] }; // admissible #clt{n}Disposizione options
  cltLato: { 1: CltLato[]; 2: CltLato[] }; // selectable #clt{n}Lato options
  cltClassi: string[]; // #clt{n}Classe and per-layer classes (C14–C35)
};

// The texts the engine writes (warnings, notes, result cards), in the user's language.
export type EngineTexts = Translation["screwConnectorModule"]["calculation"];

export type EngineCtx = {
  v: ControlValues;
  cat: EngineCatalog;
  t: EngineTexts;
  ui: UiState;
  options: EngineOptions;
  solutionsRunning: boolean; // HTML global: true while findCompatibleSolutions runs (washer height ignored)
  partialAStrict: boolean; // HTML global: smooth-shank prefilter A ≥ t1−20, only in the solution search
  manualInputMode: boolean; // HTML global: false after "Usa questa" until the user edits a field
};

export function createCtx(v: ControlValues, cat: EngineCatalog, t: EngineTexts): EngineCtx {
  return {
    v,
    cat,
    t,
    ui: createUiState(t),
    options: {
      matTipo: { 1: [], 2: [] },
      matSpecie: { 1: [], 2: [] },
      matClasse: { 1: [], 2: [] },
      families: [],
      diameters: [],
      lengths: [],
      washers: [],
      cltDisposizione: { 1: [], 2: [] },
      cltLato: { 1: ["narrow", "wide"], 2: ["narrow", "wide"] },
      cltClassi: [],
    },
    solutionsRunning: false,
    partialAStrict: false,
    manualInputMode: true,
  };
}

// Deep-enough copy of a context (controls + flags) for the solution search, which mutates the controls.
export function cloneCtx(ctx: EngineCtx): EngineCtx {
  return {
    ...ctx,
    v: {
      ...ctx.v,
      clt1UserLayers: ctx.v.clt1UserLayers.map((l) => ({ ...l })),
      clt2UserLayers: ctx.v.clt2UserLayers.map((l) => ({ ...l })),
    },
    // visibility/disabled flags are read by requiredEmptyFields (calcBlockReason) during the search
    ui: {
      ...ctx.ui,
      visible: { ...ctx.ui.visible },
      disabled: { ...ctx.ui.disabled },
      texts: { ...ctx.ui.texts },
      textTone: { ...ctx.ui.textTone },
      labels: { ...ctx.ui.labels },
      limits: { ...ctx.ui.limits },
      results: null,
      violations: null,
    },
    options: { ...ctx.options },
  };
}

// Connection-type predicates (HTML isThreeEl()… read #materiale1).
export const isThreeEl = (t: ConnectionType | string) => t === "legno3" || t === "legno3d" || t === "pannello3d";
// double shear: one screw through the 3 members (head on 2, tip in 3)
export const isThreeD = (t: ConnectionType | string) => t === "legno3d" || t === "pannello3d";
// two single shears (screws from both sides)
export const isThreeS = (t: ConnectionType | string) => t === "legno3";
// "two sides" geometry (central timber, members 1 and 3 identical plates/panels at the sides)
export const isSteel3 = (t: ConnectionType | string) => t === "acciaio3" || t === "pannello3";
export const isPanelD = (t: ConnectionType | string) => t === "pannello3d";
export const isPanel3 = (t: ConnectionType | string) => t === "pannello3";
export const isSteelMat = (t: ConnectionType | string) => t === "acciaio" || t === "acciaio3";
export const isPanelMat = (t: ConnectionType | string) => t === "pannello" || t === "pannello3" || t === "pannello3d";
export const isTwoSides = (t: ConnectionType | string) => isThreeS(t) || isSteel3(t);

// parseInt on a numeric control (the HTML parses the field text)
export const pInt = (x: number): number => (Number.isFinite(x) ? Math.trunc(x) : NaN);
